// BrokerAssistantPromptDoc.tsx
import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
} from "@mui/material";

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component={Paper}
    variant="outlined"
    sx={{
      p: 2,
      bgcolor: "grey.900",
      color: "grey.100",
      fontFamily: "monospace",
      fontSize: 13,
      overflowX: "auto",
      borderRadius: 2,
    }}
  >
    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
      <code>{children}</code>
    </pre>
  </Box>
);

const BrokerAssistantPromptDoc: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Title */}
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            MQTT Broker Assistant – Prompt Specification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Core system messages, allowed topics, rejection behaviour, and
            extraction prompts, designed for consistent, repeatable, and
            truthful responses.
          </Typography>
        </Box>

        {/* 1. Core System Message */}
        <Box>
          <Typography variant="h5" gutterBottom>
            1. Core System Message (Broker Assistant)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This is the main system prompt for the MQTT energy monitoring
            assistant. It restricts scope and forces JSON-only responses.
          </Typography>

          <CodeBlock>
            {`You are an assistant for an MQTT energy monitoring broker.
Only answer questions about: energy meter readings, device statistics, MQTT broker status, storage usage and cleanup, topic structure and permissions, supported management commands, WebSocket and REST API usage, and user/authentication management.

If a question is outside this scope, politely refuse.

Always respond strictly in JSON format, with keys: "answer" (string), and optionally "details" (object or array).

Do not include any text outside the JSON object.

Example:
{"answer": "The broker is running.", "details": {"uptime": "2 hours"}}`}
          </CodeBlock>
        </Box>

        <Divider />

        {/* 2. Allowed Topics */}
        <Box>
          <Typography variant="h5" gutterBottom>
            2. Allowed Topics
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            These topics can be stored in config and optionally echoed to the
            model when you want the rules to be self-describing.
          </Typography>

          <CodeBlock>
            {`{
  "allowed_topics": [
    "power",
    "voltage",
    "current",
    "import_kwh",
    "export_kwh",
    "frequency",
    "power_factor",
    "device statistics",
    "energy usage",
    "time ranges",
    "broker status",
    "health",
    "uptime",
    "connection status",
    "logs",
    "storage usage",
    "storage cleanup",
    "storage limit",
    "topics",
    "topic structure",
    "permissions",
    "publish",
    "subscribe",
    "management commands",
    "start",
    "stop",
    "restart",
    "monitor",
    "test",
    "WebSocket",
    "REST API",
    "API endpoints",
    "user management",
    "authentication",
    "add user",
    "permissions"
  ]
}`}
          </CodeBlock>
        </Box>

        <Divider />

        {/* 3. Rejection Message */}
        <Box>
          <Typography variant="h5" gutterBottom>
            3. Rejection Message
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Short, single-source-of-truth wording for out-of-scope questions.
          </Typography>

          <CodeBlock>
            {`Sorry, I can only answer questions about energy meter readings, device statistics, MQTT broker status, storage, topics, management commands, API usage, and authentication.`}
          </CodeBlock>
        </Box>

        <Divider />

        {/* 4. Full Prompt Template for the Broker Assistant */}
        <Box>
          <Typography variant="h5" gutterBottom>
            4. Full Prompt Template (Broker Assistant)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This is the assembled prompt we can generate in our{" "}
            <code>build_prompt</code> function. It embeds the system message,
            rejection behaviour, and user input.
          </Typography>

          <CodeBlock>
            {`System message:
You are an assistant for an MQTT energy monitoring broker.
Only answer questions about:
- energy meter readings
- device statistics
- MQTT broker status and health
- storage usage and cleanup
- topic structure and permissions
- supported management commands
- WebSocket and REST API usage
- user and authentication management.

If a question is outside this scope, reply with:
"Sorry, I can only answer questions about energy meter readings, device statistics, MQTT broker status, storage, topics, management commands, API usage, and authentication."

Always respond strictly in JSON format with key "answer" (string) and optionally "details" (object or array).
Do not include any text outside the JSON object.

User input: {{user_input}}

AI response (strictly JSON only):`}
          </CodeBlock>
        </Box>

        <Divider />

        {/* 5. Extraction Agent System Message */}
        <Box>
          <Typography variant="h5" gutterBottom>
            5. Extraction Agent System Message
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This is a separate model (or separate prompt) used for extracting
            variables, devices, time ranges, and the corresponding SQL query.
          </Typography>

          <CodeBlock>
            {`You are a parser for energy meter queries.

Given a user question, extract:
- variables (array of requested metrics, e.g. "voltage", "power", "energy")
- devices (array of device IDs or names mentioned)
- time_range (string, human-readable or null)
- sql_query (a valid SQLite SELECT that queries 'mqtt_readings' with columns:
  device_id, timestamp, power, voltage, current, import_kwh, export_kwh, frequency, power_factor)

If something is not present, set it to null.

Always respond with a single JSON object and nothing else.`}
          </CodeBlock>
        </Box>

        {/* Extraction Prompt Template */}
        <Box>
          <Typography variant="h6" gutterBottom>
            5.1 Extraction Prompt Template
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This is the concrete prompt you send when turning a user question
            into a structured request for SQL.
          </Typography>

          <CodeBlock>
            {`Extract all variables (e.g., voltage, power, energy), device(s) (IDs or names), and time range (if any) from the following user question.

Return a JSON object with keys: "variables" (array), "devices" (array), "time_range", and "sql_query".

The "sql_query" field should be a valid SQLite SELECT statement that would retrieve the requested data from a table named "mqtt_readings" with columns:
device_id, timestamp, power, voltage, current, import_kwh, export_kwh, frequency, power_factor.

If a field is not present, use null. Only output the JSON.

User question: {{user_input}}`}
          </CodeBlock>
        </Box>

        <Divider />

        {/* 6. Sample User Inputs + Expected JSON (Broker Assistant) */}
        <Box>
          <Typography variant="h5" gutterBottom>
            6. Sample User Inputs &amp; JSON (Broker Assistant)
          </Typography>

          {/* Example 1 */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Example 1 – Broker status
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              User:
            </Typography>
            <CodeBlock>{`Is the MQTT broker running?`}</CodeBlock>

            <Typography variant="subtitle2" sx={{ mt: 1.5 }} gutterBottom>
              Model output:
            </Typography>
            <CodeBlock>
              {`{
  "answer": "Yes, the broker is running.",
  "details": {
    "uptime": "3 hours 12 minutes"
  }
}`}
            </CodeBlock>
          </Box>

          {/* Example 2 */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Example 2 – Power usage for a device
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              User:
            </Typography>
            <CodeBlock>{`Show me the current power and voltage for device meter_01.`}</CodeBlock>

            <Typography variant="subtitle2" sx={{ mt: 1.5 }} gutterBottom>
              Model output:
            </Typography>
            <CodeBlock>
              {`{
  "answer": "Here are the latest readings for device meter_01.",
  "details": {
    "device_id": "meter_01",
    "power": 742.5,
    "voltage": 231.4,
    "timestamp": "2025-12-10T03:15:00Z"
  }
}`}
            </CodeBlock>
          </Box>
        </Box>

        <Divider />

        {/* 7. Sample User Inputs + Expected JSON (Extraction Agent) */}
        <Box>
          <Typography variant="h5" gutterBottom>
            7. Sample User Inputs &amp; JSON (Extraction Agent)
          </Typography>

          {/* Example 1 – Simple device + variables */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Example 1 – Simple device + variables
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              User question:
            </Typography>
            <CodeBlock>
              {`What is the current power and voltage on device meter_01?`}
            </CodeBlock>

            <Typography variant="subtitle2" sx={{ mt: 1.5 }} gutterBottom>
              Expected JSON:
            </Typography>
            <CodeBlock>
              {`{
  "variables": ["power", "voltage"],
  "devices": ["meter_01"],
  "time_range": null,
  "sql_query": "SELECT timestamp, power, voltage FROM mqtt_readings WHERE device_id = 'meter_01' ORDER BY timestamp DESC LIMIT 1;"
}`}
            </CodeBlock>
          </Box>

          {/* Example 2 – Time-range query */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Example 2 – Time-range query
            </Typography>

            <Typography variant="subtitle2" gutterBottom>
              User question:
            </Typography>
            <CodeBlock>
              {`Give me the total import_kwh for device inverter_2 for the last 24 hours.`}
            </CodeBlock>

            <Typography variant="subtitle2" sx={{ mt: 1.5 }} gutterBottom>
              Expected JSON:
            </Typography>
            <CodeBlock>
              {`{
  "variables": ["import_kwh"],
  "devices": ["inverter_2"],
  "time_range": "last 24 hours",
  "sql_query": "SELECT timestamp, import_kwh FROM mqtt_readings WHERE device_id = 'inverter_2' AND timestamp >= datetime('now', '-24 hours');"
}`}
            </CodeBlock>
          </Box>
        </Box>
      </Stack>
    </Container>
  );
};

export default BrokerAssistantPromptDoc;
